import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FiX } from 'react-icons/fi';
import './CancelAppointmentModal.css';

const REASONS = {
  user: [
    "Schedule conflict",
    "Personal emergency",
    "Symptoms already resolved",
    "Will visit another clinic",
    "Transportation issue",
    "Other"
  ],
  staff: [
    "Patient did not confirm attendance",
    "Clinic scheduling conflict",
    "Medical Professional unavailable",
    "Duplicate appointment detected",
    "Requested service unavailable",
    "Appointment exceeds clinic hours",
    "Other"
  ]
};

const CancelAppointmentModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  appointment, 
  theme = 'user',
  isSubmitting = false
}) => {
  const [selectedReason, setSelectedReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setSelectedReason("");
      setOtherReason("");
      setError("");
    }
  }, [isOpen]);

  if (!isOpen || !appointment) return null;

  const validate = () => {
    if (!selectedReason) {
      setError("Please select a reason for cancellation.");
      return false;
    }

    if (selectedReason === "Other") {
      const trimmed = otherReason.trim();
      // Sanitize: remove HTML tags
      const sanitized = trimmed.replace(/<[^>]*>?/gm, '');
      
      if (!sanitized) {
        setError("Please provide a specific reason.");
        return false;
      }
      if (sanitized.length < 10) {
        setError("Reason must be at least 10 characters long.");
        return false;
      }
      if (sanitized.length > 500) {
        setError("Reason must not exceed 500 characters.");
        return false;
      }
    }

    setError("");
    return true;
  };

  const handleConfirm = () => {
    if (validate()) {
      const finalReason = selectedReason === "Other" ? otherReason.trim() : selectedReason;
      onConfirm(appointment, finalReason);
    }
  };

  const reasonsList = REASONS[theme] || REASONS.user;

  return createPortal(
    <div className="cancel-modal-backdrop" onClick={onClose}>
      <div 
        className={`cancel-modal-card ${theme}-theme`} 
        onClick={e => e.stopPropagation()}
      >
        <div className="cancel-modal-header">
          <h3>Cancel Appointment</h3>
          <button className="cancel-modal-close-btn" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>

        <div className="cancel-modal-body">
          <p className="cancel-modal-description">
            {theme === 'user' 
              ? "Are you sure you want to cancel this appointment? Please let us know the reason."
              : `Are you sure you want to cancel ${appointment.patientName}'s appointment?`}
          </p>

          <div className="cancel-modal-form-group">
            <label className="cancel-modal-label">Reason for Cancellation</label>
            <select 
              className="cancel-modal-select"
              value={selectedReason}
              onChange={(e) => {
                setSelectedReason(e.target.value);
                setError("");
              }}
            >
              <option value="">Select a reason</option>
              {reasonsList.map(reason => (
                <option key={reason} value={reason}>{reason}</option>
              ))}
            </select>
          </div>

          {selectedReason === "Other" && (
            <div className="cancel-modal-form-group">
              <label className="cancel-modal-label">Please specify</label>
              <textarea 
                className="cancel-modal-textarea"
                placeholder="Type your reason here..."
                value={otherReason}
                onChange={(e) => {
                  setOtherReason(e.target.value);
                  setError("");
                }}
                autoFocus
              />
              <div className="cancel-modal-textarea-footer">
                {error && <p className="cancel-modal-error-text">{error}</p>}
                <span className={`cancel-modal-char-count ${otherReason.length > 500 ? 'limit-reached' : ''}`}>
                  {otherReason.length}/500
                </span>
              </div>
            </div>
          )}
          
          {selectedReason !== "Other" && error && (
            <p className="cancel-modal-error-text">{error}</p>
          )}
        </div>

        <div className="cancel-modal-footer">
          <button 
            className="cancel-modal-btn cancel-modal-btn-secondary" 
            onClick={onClose}
            disabled={isSubmitting}
          >
            {theme === 'user' ? "Keep Appointment" : "Go Back"}
          </button>
          <button 
            className="cancel-modal-btn cancel-modal-btn-danger" 
            onClick={handleConfirm}
            disabled={isSubmitting || !selectedReason || (selectedReason === "Other" && otherReason.trim().length < 10)}
          >
            {isSubmitting ? "Cancelling..." : "Confirm Cancellation"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CancelAppointmentModal;
